import { ReactText } from 'react'

import QRCode from 'qrcode'
import { Flip, toast } from 'react-toastify'
import { DefaultTheme } from 'styled-components'
// eslint-disable-next-line import/no-unresolved
import { type Tizen } from 'tizen-common-web'

import { isDevMode, isEnv } from '@sportworld/js-sdk/src/envs/envs'
import {
  EventType,
  eventTypes,
  VideoSourceType,
} from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'
import { PageType } from '@sportworld/js-sdk/src/types/servicesTypes/pageTypes'
import {
  getContentAccessFlags,
  getPageParams,
} from '@sportworld/js-sdk/src/utils'

import { getChannelSources, getEventSources } from '@/actions/actions'
import { HtmlWidgetSizeType } from '@/components/HtmlWidget/types'
import { eventCategory, pageTitle } from '@/constants/analytics'
import { LineTileShape } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { imageType } from '@/constants/icons'
import { LoginSessionType } from '@/constants/login'
import { actionDataVodMock } from '@/constants/mock'
import { paths } from '@/constants/paths'
import i18n from '@/i18n'
import { getVersion, isChannelTunable } from '@/platform/platform'
import { platformName } from '@/platform/platformUtils'
import { PageService } from '@/service'
import useStore from '@/store/useStore'
import {
  ChannelType,
  ConfigType,
  ImageSourceType,
  LineType,
  PaletteType,
} from '@/types/servicesTypes/configTypes'
import {
  FindImgByWidthOptionsType,
  GridBoxType,
  TizenChannel,
} from '@/types/utilsTypes'

const tizen = (window as { [key: string]: any })['tizen'] as Tizen
const gtagIframeWindow = (window as { [key: string]: any })[
  'gtagIframeWindow'
] as any

const { setIsAppHidden } = useStore.getState().common

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    isDevMode && console.log('[utils] document hidden')
    setIsAppHidden(true)
    sendEventToGTAG(eventCategory.APP_VISIBILITYCHANGE, { state: 'hidden' })
  } else {
    isDevMode && console.log('[utils] document resumed')
    setIsAppHidden(false)
    sendEventToGTAG(eventCategory.APP_VISIBILITYCHANGE, { state: 'resumed' })
  }
})

/**
 * The best suitable image width.
 * Used to find the nearest matching object in data images sources array
 */
export const findNearestByImgWidth = (
  data: Array<ImageSourceType> | undefined,
  targetWidth: number,
  options: FindImgByWidthOptionsType = {},
) => {
  const { prioritizeSvg, targetAspectRatio } = options
  if (data?.length === 0 || !data) {
    return { format: '', width: 0, height: 0, url: '', darkTheme: false }
  }
  const svg = data.find((el) => el.format.toLowerCase() === imageType.VECTOR)
  let filteredData = data
  let res
  if (prioritizeSvg && svg) {
    return svg
  }

  if (targetAspectRatio) {
    const width = +targetAspectRatio.split('/')[0]
    const height = +targetAspectRatio.split('/')[1]
    const aspectRatio = width && height && width / height
    if (aspectRatio) {
      filteredData = filteredData.filter(
        (el) => (el.width / el.height).toFixed(1) === aspectRatio.toFixed(1),
      )
    }
  }

  if (filteredData.length) {
    res = filteredData.reduce((acc, obj) =>
      Math.abs(targetWidth - obj.width) < Math.abs(targetWidth - acc.width)
        ? obj
        : acc,
    )
  }

  if (res && res.width > targetWidth * 3) {
    const oversizeValue = (res.width / targetWidth).toFixed(1)
    res = { ...res, oversize: `${oversizeValue}` }
  }

  /* return {
    format: '',
    width: 0,
    height: 0,
    url: `https://picsum.photos/${targetWidth}`,
    // url: '',
    darkTheme: false,
  } */

  return res || svg
}

const offset = 1.05
const aspectRatiosPerShape = {
  [LineTileShape.ASPECT16x9]: [1.78 / offset, 1.78 * offset],
  [LineTileShape.SQUARE]: [1 / offset, offset],
  [LineTileShape.CIRCLE]: [1 / offset, offset],
  [LineTileShape.ASPECT2x3]: [0.67 / offset, 0.67 * offset],
  [LineTileShape.ASPECT9x16]: [0.56 / offset, 0.56 * offset],
}
type FindBestImageParams = { targetWidth?: number; targetShape?: LineTileShape }
export const findBestImage = (
  data: Array<ImageSourceType> | undefined,
  {
    prioritizeSvg,
    targetWidth = window.innerWidth,
    targetShape = LineTileShape.ASPECT16x9,
  }: FindImgByWidthOptionsType & FindBestImageParams = {},
) => {
  if (data?.length === 0 || !data) {
    return { format: '', width: 0, height: 0, url: '', darkTheme: false }
  }
  const aspectRatioRange = aspectRatiosPerShape[targetShape]

  if (prioritizeSvg) {
    const svgArray = data.filter(
      (el) => el.format.toLowerCase() === imageType.VECTOR,
    )
    if (svgArray.length) {
      data = svgArray
    }
  }

  let shapeSensitiveRes: ImageSourceType | null = null
  let fallBackRes: ImageSourceType | null = null

  data.forEach((el) => {
    const aspectRatio = el.width / el.height
    const isPassingAspectRatio =
      aspectRatio > aspectRatioRange[0] && aspectRatio < aspectRatioRange[1]
    if (isPassingAspectRatio) {
      if (!shapeSensitiveRes) {
        shapeSensitiveRes = el
      } else {
        shapeSensitiveRes =
          Math.abs(targetWidth - shapeSensitiveRes.width) <
          Math.abs(targetWidth - el.width)
            ? shapeSensitiveRes
            : el
      }
    }
    if (!fallBackRes) {
      fallBackRes = el
      return
    }
    fallBackRes =
      Math.abs(targetWidth - fallBackRes.width) <
      Math.abs(targetWidth - el.width)
        ? fallBackRes
        : el
  })

  if (!fallBackRes) {
    return { format: '', width: 0, height: 0, url: '', darkTheme: false }
  }

  let res: ImageSourceType = shapeSensitiveRes || fallBackRes

  if (res.width > targetWidth * 3) {
    const oversizeValue = (res.width / targetWidth).toFixed(1)
    res = { ...res, oversize: `${oversizeValue}` }
  }

  return res
}

export const getTimezoneOffsetReversed = () => {
  const { useUtcOffsetMins } = useStore.getState().common.appConfig

  return useUtcOffsetMins ? new Date().getTimezoneOffset() * -1 : 0
}

export const generateQrCode = (
  path: string,
  qrCodeEl: HTMLCanvasElement,
  qrSize = 216,
) => {
  QRCode.toCanvas(qrCodeEl, path, { width: qrSize }, (error) => {
    if (error) {
      return
    }
  })
}

export const formatSecondsToTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hours * 3600) / 60)
  const secondsLeftRaw = seconds - hours * 3600 - minutes * 60
  const secondsLeft =
    secondsLeftRaw < 10 ? `0${secondsLeftRaw}` : secondsLeftRaw
  return `${minutes}:${secondsLeft}`
}

/**
 *
 * @param tizenChannels TizenChannel[]
 * @param channel ChannelType
 * @returns TizenChannel | undefined
 */
export const findChannelByNames = (
  tizenChannels: TizenChannel[],
  channel: ChannelType,
) => {
  const tizenChannel = tizenChannels.find(
    (ch: TizenChannel) =>
      channel.knownNames?.includes(ch.channelName + ' HD') ||
      channel.knownNames?.includes(ch.channelName),
  )

  if (tizenChannel) {
    isDevMode &&
      console.log('[utils] findChannelByNames', tizenChannel.channelName)
    isDevMode &&
      showInfo(
        `[DevMode] Found Channel ${tizenChannel.channelName} or ${tizenChannel.channelName} HD`,
      )
  }

  return tizenChannel
}

export const findChannelByLocator = (
  tizenChannels: TizenChannel[],
  channel: ChannelType,
) => {
  const tizenChannel = tizenChannels.find((ch: TizenChannel) =>
    channel.locators?.includes(ch.locator),
  )
  if (tizenChannel) {
    isDevMode &&
      console.log('[utils] findChannelByLocator', tizenChannel.locator)
    isDevMode && showInfo(`Found locator: ${tizenChannel.locator}`)
  }
  return tizenChannel
}

export const tizenDeepLinkHandler = async () => {
  /**
   * VOD mock
   * @use debug true to activate
   */
  const debug = false
  if (debug && JSON.parse(actionDataVodMock).id) {
    const dataFromPreviewTiles = JSON.parse(actionDataVodMock)
    return { dataFromPreviewTiles }
  }

  const requestedAppControl = tizen?.application
    ?.getCurrentApplication()
    .getRequestedAppControl()

  let actionData

  if (requestedAppControl) {
    const appControlData = requestedAppControl.appControl.data
    for (let i = 0; i < appControlData.length; i++) {
      if (appControlData[i].key == 'PAYLOAD') {
        const payloadData = appControlData[i].value[0]
          .replace(/\\(u0022)/g, '\\\\u0022')
          .replace(/\\(n)/g, ' ')
        try {
          actionData = JSON.parse(payloadData).values
          const parsedActionData = JSON.parse(actionData)
          if (parsedActionData.id) {
            return { dataFromPreviewTiles: parsedActionData }
          }
          if (parsedActionData.assetId) {
            return { dataFromUniversalGuide: parsedActionData.assetId }
          }
          if (parsedActionData.SCOUPONCODE) {
            return { dataFromPromotion: parsedActionData }
          }
        } catch (err) {
          showError('Json Data is not valid')
          return
        }
      }
    }
  } else {
    isDevMode && console.log('no req app control')
    return
  }
}

export const showError = (text?: string) => {
  if (!text) {
    return false
  }
  toast.error(text, {
    position: 'bottom-right',
    autoClose: 20000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    transition: Flip,
  })
}

export const showWarning = (text?: string) => {
  if (!text) {
    return false
  }
  toast.warn(text, {
    position: 'bottom-right',
    autoClose: 20000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    transition: Flip,
  })
}

export const showSuccess = (text?: string) => {
  if (!text) {
    return false
  }
  toast.success(text, {
    position: 'bottom-right',
    autoClose: 20000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    transition: Flip,
  })
}

export const showInfo = (text?: string) => {
  if (!text) {
    return false
  }
  toast.info(text, {
    position: 'bottom-right',
    autoClose: 20000,
    hideProgressBar: true,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    progress: 0,
    transition: Flip,
  })
}

export const showLoading = (text?: string) => {
  if (!text) {
    return false
  }
  return toast.loading(text, {
    position: 'bottom-right',
    autoClose: false,
  })
}

export const closeLoading = (toastId: ReactText, text: string) => {
  toast.update(toastId, {
    render: text,
    type: toast.TYPE.SUCCESS,
    autoClose: 5000,
    isLoading: false,
  })
}

export const getVideoForChannel = async (
  activeChannel: ChannelType,
  isRegisteredUser?: boolean,
  isChannelWatchable?: boolean,
  hasVideoBlockedOverlay?: boolean,
  currentChannelEvent?: EventType | null,
) => {
  const { isAccessible, isPremium, isPPVEvent } = getContentAccessFlags(
    currentChannelEvent,
    activeChannel,
    isRegisteredUser,
  )
  const isPremiumContent = isPremium || isPPVEvent

  let channelVideoSources: VideoSourceType[] | undefined = isPremiumContent
    ? undefined
    : activeChannel?.video?.sources

  isDevMode &&
    channelVideoSources &&
    showInfo('VideoSources from Channel object')
  if (activeChannel && !channelVideoSources && isAccessible) {
    try {
      if (activeChannel.userRights?.canAccess) {
        const channelPlayback = await getChannelSources(activeChannel.id)
        channelVideoSources = channelPlayback.sources
        isDevMode && showInfo('VideoSources from backend (Channel)')
      } else if (currentChannelEvent) {
        const channelPlayback = await getEventSources(currentChannelEvent.id)
        channelVideoSources = channelPlayback.sources
        isDevMode && showInfo('VideoSources from backend (Event)')
      }
    } catch {
      isDevMode && showInfo('Error get VideoSources from backend')
    }
  }

  const videoUrl = channelVideoSources?.[0]?.url
  const freeVideoUrl = activeChannel?.freeVideo?.sources?.[0]?.url
  let seekToLiveEdge = true
  let videoSources
  let onEnded
  if (isAccessible && isChannelWatchable && videoUrl) {
    videoSources = channelVideoSources
    onEnded = activeChannel?.video?.onEnded
  } else if (
    !isAccessible &&
    isChannelWatchable &&
    freeVideoUrl &&
    hasVideoBlockedOverlay
  ) {
    videoSources = activeChannel?.freeVideo?.sources
    onEnded = activeChannel?.freeVideo?.onEnded
    seekToLiveEdge = false
  }
  const videoSource = videoSources?.[0]
  let videoFormat
  if (videoSource && 'format' in videoSource) {
    videoFormat = videoSource.format
  } else {
    videoFormat = videoSource?.type
  }
  return {
    src: videoSource?.url,
    format: videoFormat || '',
    onEnded,
    seekToLiveEdge,
    adSettings: activeChannel.adSettings,
    partnerIds: currentChannelEvent?.partnerIds || activeChannel?.partnerIds,
  }
}

export const checkIsChannelPlayable = (
  event: EventType,
  hasEventVideoSources: boolean,
  isAccessible: boolean,
) => {
  const isChannelWatchable = !!event.watchable

  switch (event.type) {
    case eventTypes.YOUTUBE:
      return !!event.id
    case eventTypes.VOD: {
      if (!isChannelWatchable) {
        return false
      } else if (!isAccessible) {
        return true
      }
      return hasEventVideoSources
    }
    case eventTypes.CHANNEL: {
      if (!isChannelWatchable) {
        return false
      } else if (!isAccessible) {
        return true
      }
      return hasEventVideoSources
    }
    default:
      return false
  }
}

export const checkIsValidDeepLink = (event: EventType) => {
  if (!isTizen()) {
    return false
  }
  return !!event.deepLink?.tizen?.appId
}

/**
 * @deprecated
 * @description The TV Channel API is not available on Tizen from mid 2024
 */
export const checkIsChannelTunable = (eventChannel: Nullable<ChannelType>) => {
  if (!isTizen() || !eventChannel) {
    return false
  }

  const { locators, knownNames } = eventChannel

  const userHasTVData = Boolean(locators || knownNames)

  const channelIsTunable = isChannelTunable(eventChannel)

  return userHasTVData && channelIsTunable
}

// TODO: move to analyticsUtils.ts -> will produce 45 changes in 13 files
export async function sendEventToGTAG(
  name: string,
  props?: { [key: string]: string | number | undefined },
) {
  if (!isTVOS() && isEnv('prod')) {
    isDevMode && console.log('🐰 GTAG (disabled)', { name, props })
    return
  }

  try {
    // showInfo(`send to gtag ${name} ${JSON.stringify(props)}`)
    gtagIframeWindow.postMessage({ name, props }, '*')
  } catch (error) {}
}

const getPageTitleByPathName = (pathname: string) => {
  if (pathname.includes('/event') && !pathname.includes('/player')) {
    return pageTitle['/event']
  }
  if (pathname.includes('/page') || pathname.includes('/favorites')) {
    return pageTitle['/page']
  }
  if (pathname.includes('/list')) {
    return pageTitle['/list']
  }
  return pageTitle[pathname as keyof object]
}

export async function sendPageImpressionToGTAG(pathname: string, tab?: string) {
  const ignoredPaths = ['/', '/index.html', '/favorites']
  const title = getPageTitleByPathName(pathname)

  if (!isTVOS() || ignoredPaths.includes(pathname)) {
    return false
  }

  if (title === pageTitle['/page']) {
    const pageId = pathname.split('/').pop()
    pageId &&
      PageService.getPageById(pageId)
        .then((page) => {
          sendEventToGTAG(eventCategory.PAGE_IMPRESSION, {
            id: pageId,
            name: page.name,
            type: page.pageType,
            tab: tab,
          })
        })
        .catch(() => {
          sendEventToGTAG(eventCategory.PAGE_IMPRESSION, {
            id: pageId,
            name: title,
          })
        })
  } else if (title === pageTitle['/list']) {
    const listProps = pathname.split('/')
    const name = listProps.pop()
    const lineId = listProps.pop()
    sendEventToGTAG(eventCategory.PAGE_IMPRESSION, {
      id: lineId,
      name: name,
      type: 'ViewMore',
    })
  } else {
    const eventId =
      title === pageTitle['/event'] ? pathname.split('/').pop() : undefined
    sendEventToGTAG(eventCategory.PAGE_IMPRESSION, {
      id: eventId,
      name: title,
      type: title === pageTitle['/home'] ? 'ClassicHome' : 'normal',
    })
  }
}

export function isFireos() {
  return platformName === 'FireOS'
}

export function isTizen() {
  return platformName === 'Tizen'
}

export function isWebos() {
  return platformName === 'WebOS'
}

export function isWebOSVersionHigherOrEqual(version: string) {
  if (!isWebos()) {
    return
  }
  const webOSVersion = getVersion()
  return parseFloat(webOSVersion) >= parseFloat(version)
}

export function isTVOS() {
  return isTizen() || isWebos() || isFireos()
}

export const getPercentageRangeOfViewed = (
  duration?: number,
  currentTime?: number,
) => {
  const videoDuration = Math.ceil(duration || 0)
  const videoCurrentTime = Math.ceil(currentTime || 0)
  const percentageFull =
    videoDuration && (videoCurrentTime / videoDuration) * 100
  const percentage = +percentageFull.toFixed(2)
  if (percentage < 20) {
    return '0-20%'
  }
  if (percentage < 40) {
    return '20-40%'
  }
  if (percentage < 60) {
    return '40-60%'
  }
  if (percentage < 80) {
    return '60-80%'
  }
  if (percentage <= 100) {
    return '80-100%'
  } else {
    return '0-20%'
  }
}

export const getActualSessionType = (sessionType: LoginSessionType) => {
  return `&type=${sessionType}`
}

export const getDateFormat = () => {
  const isUSFormat = i18n.language.includes('US')
  return isUSFormat ? 'eeeeee, MM/dd/yyyy' : 'eeeeee, dd/MM/yyyy'
}
export const getTimeFormat = () => {
  const isUSFormat = i18n.language.includes('US')
  return isUSFormat ? 'hh:mm a' : 'HH:mm'
}

export const transpile = (
  array: GridBoxType[],
  maxHeight: number,
  maxWidth: number,
  itemsLimit?: number,
) => {
  const grid = [[[]]]

  let colIndex = -1,
    rowIndex = -1,
    currentCol: any,
    currentRow: any,
    targetCell: any,
    box: GridBoxType,
    createNewCol = false,
    createNewRow = false

  if (itemsLimit) {
    array.length = itemsLimit
  }

  for (let i = 0; i < array.length; i++) {
    box = array[i]
    box.size = box.w * box.h

    const isValid = box.size && box.w <= maxWidth && box.h <= maxHeight

    if (!isValid) {
      continue
    }

    createNewCol =
      !grid[colIndex] ||
      box.w > currentCol.width ||
      box.size > currentCol.free ||
      (box.h > currentRow.height && box.size > currentCol.free)

    if (createNewCol) {
      colIndex++
      rowIndex = -1
      currentCol = grid[colIndex] = []
      currentCol.width = box.w
      currentCol.free = currentCol.capacity = currentCol.width * maxHeight
    }

    createNewRow = !grid[colIndex][rowIndex] || box.size > currentRow.free

    if (createNewRow) {
      rowIndex++
      currentRow = grid[colIndex][rowIndex] = []
      currentRow.height = box.h
      currentRow.free = currentRow.capacity = currentCol.width * box.h
    }

    targetCell = grid[colIndex][rowIndex]

    targetCell.push(box)

    currentRow.free -= box.size
    currentCol.free -= box.size
  }

  return grid
}

export const showDevModePane = (() => {
  return import.meta.env.REACT_APP_SHOW_DEV_MODE_PANE === 'true'
})()

export const isPointerMode = (() => {
  return import.meta.env.REACT_APP_POINTING_DEVICE_SUPPORT === 'true'
})()

export const getLineTitle = (groupData: Array<LineType>, sliderId: string) => {
  return groupData.find((el: LineType) => el.lineContextId === sliderId)?.name
}

export const getLineCategoryId = (
  groupData: Array<LineType>,
  sliderId: string,
) => {
  return groupData.find((el: LineType) => el.lineContextId === sliderId)
    ?.categoryId
}

const isHex = (color: string) => {
  return /^#([A-Fa-f0-9]{3}){1,2}$/.test(color)
}

const hexToRgb = (color?: string) => {
  if (!color) {
    return ''
  }
  let c = color.substring(1).split('')
  let r = 0,
    g = 0,
    b = 0
  c = c.length === 3 ? [c[0], c[0], c[1], c[1], c[2], c[2]] : c
  r = +('0x' + c[0] + c[1])
  g = +('0x' + c[2] + c[3])
  b = +('0x' + c[4] + c[5])
  const res = [r, g, b].join(', ')

  return res
}

const hexToRgbA = (color: string) => {
  let c = color.substring(1).split('')
  let r = 0,
    g = 0,
    b = 0
  c = c.length === 3 ? [c[0], c[0], c[1], c[1], c[2], c[2]] : c
  r = +('0x' + c[0] + c[1])
  g = +('0x' + c[2] + c[3])
  b = +('0x' + c[4] + c[5])
  return 'rgba(' + [r, g, b].join(',') + ',1)'
}

const changeColorOpacity = (
  color: string | undefined,
  opacity: number,
  debug?: boolean,
) => {
  if (!color) {
    return
  }
  const rgba = isHex(color)
    ? hexToRgbA(color)
    : color.includes('rgba')
    ? color
    : color.replace(')', ',1)').replace('rgb', 'rgba')
  const newColor = rgba.split(',')
  newColor[3] = opacity + ')'
  const res = newColor.join(',')

  debug && console.log('color', res)

  return res
}

export const paletteClone = (obj: { [key: string]: any }) => {
  if (obj === null || typeof obj != 'object') {
    return obj
  }
  if (typeof obj === 'object') {
    const clone: { [key: string]: any } = {}
    for (const prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        clone[prop] = paletteClone(obj[prop])
      }
    }
    return clone
  }
}

export const updateObject = (
  obj: { [key: string]: any },
  update: { [key: string]: any },
) => {
  if (typeof update === 'object') {
    for (const prop in update) {
      if (Object.prototype.hasOwnProperty.call(update, prop)) {
        if (typeof update[prop] === 'object') {
          if (!obj[prop]) {
            obj[prop] = {}
          }
          obj[prop] = updateObject(obj[prop], update[prop])
        } else {
          obj[prop] = update[prop]
        }
      }
    }
  }
  return obj
}

export const createTheme = (palette: PaletteType) => {
  const theme = {
    id: palette.id,
    name: palette.name,
    colors: {
      body: palette.colors?.secondary,
      text: palette.colors?.text,
      title: palette.colors?.accent,
      link: palette.colors?.accent,
      accent: palette.colors?.accent,
      altAccent: palette.colors?.altAccent,
      // ppvText: palette.colors?.ppvText,
      // ppvBg: palette.colors?.ppvBg,
      // promotionText: palette.colors?.promotionText,
      // promotionBg: palette.colors?.promotionBg,
      badgeLiveBg: palette.colors?.badgeLiveBg,
      badgeTimeBg: palette.colors?.badgeTimeBg,
      textDark: palette.colors?.textDark,
      primary: palette.colors?.primary,
      app: {
        background: {
          start: palette.colors?.gradient?.start,
          end: palette.colors?.gradient?.end,
        },
      },
      sidebar: {
        sidebarBackground: {
          start: changeColorOpacity(palette.colors?.shade, 0.3),
          end: changeColorOpacity(palette.colors?.shade, 0.8),
        },
        blurBackground: changeColorOpacity(palette.colors?.shade, 0.1),
        border: changeColorOpacity(palette.colors?.translucent, 0.2),
        text: palette.colors?.text,
        focusedElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.2),
          border: palette.colors?.accent,
        },
        activeElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.1),
          border: changeColorOpacity(palette.colors?.translucent, 0.9),
        },
      },
      welcomeScreen: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        focusedElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.3),
          border: palette.colors?.accent,
        },
      },
      account: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        separator: changeColorOpacity(palette.colors?.translucent, 0.15),
      },
      register: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        separator: changeColorOpacity(palette.colors?.translucent, 0.15),
        backBtnBg: palette.colors?.secondary,
      },
      logout: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
      },
      homeGrid: {
        background: changeColorOpacity(palette.colors?.translucent, 0.05),
        focusedElem: {
          border: palette.colors?.accent,
        },
      },
      eventDetail: {
        text: palette.colors?.text,
        border: palette.colors?.text,
        coloredText: palette.colors?.accent,
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
      },
      developer: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        background: changeColorOpacity(palette.colors?.translucent, 0.05),
        focusedElem: {
          border: palette.colors?.accent,
          background: changeColorOpacity(palette.colors?.translucent, 0.1),
        },
      },
      calendar: {
        text: palette.colors?.text,
        border: changeColorOpacity(palette.colors?.translucent, 0.2),
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
        focusedElem: {
          background: palette.colors?.accent,
          border: palette.colors?.accent,
        },
        activeElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.9),
          border: changeColorOpacity(palette.colors?.translucent, 0.9),
        },
      },
      countdown: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        background: changeColorOpacity(palette.colors?.translucent, 0.2),
        textBackground: changeColorOpacity(palette.colors?.shade, 0.2),
        focusedElem: {
          border: palette.colors?.accent,
        },
      },
      dialogPopup: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        overlay: changeColorOpacity(palette.colors?.shade, 0.8),
        background: {
          start: palette.colors?.gradient?.start,
          end: palette.colors?.gradient?.end,
        },
        border: changeColorOpacity(palette.colors?.translucent, 0.15),
        list: {
          background: changeColorOpacity(palette.colors?.translucent, 0.05),
          focusedElem: {
            border: palette.colors?.accent,
            background: changeColorOpacity(palette.colors?.gradient?.end, 0.2),
          },
        },
      },
      dotIndicator: {
        background: changeColorOpacity(palette.colors?.text, 0.4),
        activeElem: {
          background: palette.colors?.text,
        },
      },
      sectionGrid: {
        title: palette.colors?.text,
        focusedElem: {
          border: palette.colors?.accent,
        },
      },
      player: {
        background: palette.colors?.videoBg,
        controlsBg: changeColorOpacity(palette.colors?.shade, 0.7),
        focusedElem: {
          border: palette.colors?.accent,
        },
      },
      pageHeader: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        favorite: {
          background: changeColorOpacity(palette.colors?.translucent, 0.2),
        },
      },
      settingsHeader: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
      },
      tabs: {
        text: palette.colors?.text,
        separator: changeColorOpacity(palette.colors?.translucent, 0.2),
        focusedElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.2),
          border: palette.colors?.accent,
        },
        activeElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.1),
          border: changeColorOpacity(palette.colors?.translucent, 0.9),
        },
      },
      dataWidget: {
        text: palette.colors?.text,
        accent: palette.colors?.accent,
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
        firstColBackground: changeColorOpacity(
          palette.colors?.translucent,
          0.2,
        ),
        liveText: palette.colors?.primary,
        liveBg: palette.colors?.accent,
        focusedElem: {
          border: palette.colors?.accent,
        },
        standingsPositionColors: {
          1: changeColorOpacity(palette.colors?.translucent, 0.2),
          2: changeColorOpacity(palette.colors?.translucent, 0.12),
          3: changeColorOpacity(palette.colors?.translucent, 0.05),
          4: changeColorOpacity(palette.colors?.shade, 0.05),
          5: changeColorOpacity(palette.colors?.shade, 0.15),
          6: changeColorOpacity(palette.colors?.shade, 0.26),
        },
      },
      tiles: {
        title: palette.colors?.accent,
        text: palette.colors?.text,
        liveText: palette.colors?.primary,
        liveBg: palette.colors?.accent,
        shading: hexToRgb(palette.colors?.shade),
        progressBg: changeColorOpacity(palette.colors?.shade, 0.9),
        progressLine: palette.colors?.text,
        focusedElem: {
          border: palette.colors?.accent,
        },
        activeElem: {
          background: changeColorOpacity(palette.colors?.accent, 0.3),
        },
      },
      squareTile: {
        text: palette.colors?.text,
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
        focusedElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.2),
          border: palette.colors?.accent,
        },
      },
      emptyTile: {
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
        focusedElem: {
          border: palette.colors?.accent,
        },
      },
      mediaPlayerTile: {
        background: palette.colors?.videoBg,
        focusedElem: {
          border: palette.colors?.accent,
        },
      },
      qrCode: {
        background: changeColorOpacity(palette.colors?.translucent, 0.03),
        text: palette.colors?.text,
        title: palette.colors?.text,
        link: palette.colors?.accent,
      },
      button: {
        primary: palette.colors?.primary,
        secondary: palette.colors?.secondary,
        accent: palette.colors?.accent,
        background: changeColorOpacity(palette.colors?.shade, 0.3),
        backgroundSecondary: changeColorOpacity(
          palette.colors?.translucent,
          0.1,
        ),
        backgroundIcon: changeColorOpacity(palette.colors?.translucent, 0.2),
        border: palette.colors?.text,
        text: palette.colors?.text,
        focusedElem: {
          border: palette.colors?.accent,
          background: palette.colors?.accent,
        },
      },
      toggle: {
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
        border: changeColorOpacity(palette.colors?.translucent, 0.6),
        toggleColor: changeColorOpacity(palette.colors?.translucent, 0.9),
        focusedElem: {
          border: palette.colors?.accent,
        },
        disabledElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.2),
          border: changeColorOpacity(palette.colors?.translucent, 0.2),
          toggleColor: changeColorOpacity(palette.colors?.translucent, 0.2),
        },
      },
      subscription: {
        background: changeColorOpacity(palette.colors?.shade, 0.3),
        inActiveElem: changeColorOpacity(palette.colors?.shade, 0.6),
        focusedElem: {
          border: palette.colors?.accent,
          background: changeColorOpacity(palette.colors?.shade, 0.8),
        },
      },
      search: {
        text: palette.colors?.text,
        background: changeColorOpacity(palette.colors?.translucent, 0.1),
        border: changeColorOpacity(palette.colors?.translucent, 0.7),
        focusedElem: {
          border: changeColorOpacity(palette.colors?.translucent, 1),
          background: changeColorOpacity(palette.colors?.translucent, 0.2),
        },
      },
      broadcast: {
        text: palette.colors?.text,
        title: palette.colors?.accent,
        background: palette.colors?.videoBg,
        channelBackground: changeColorOpacity(
          palette.colors?.translucent,
          0.15,
        ),
        logoBackground: changeColorOpacity(palette.colors?.translucent, 0.05),
        focusedElem: {
          background: changeColorOpacity(palette.colors?.translucent, 0.3),
          border: palette.colors?.accent,
        },
        activeElem: {
          background: changeColorOpacity(palette.colors?.accent, 0.3),
        },
      },
      linesLoader: {
        background: changeColorOpacity(palette.colors?.translucent, 0.15),
      },
    },
  }
  const updatedTheme = updateObject(theme, palette) as DefaultTheme
  return updatedTheme
}

export const showFetchTimeInfo = (name: string, start: number, end: number) => {
  const { appSettings } = useStore.getState().common
  const fetchTiming = Math.round((end - start) / 10) * 10
  isDevMode && console.log(`⏳ ${name} load time ${fetchTiming} ms`)

  appSettings.isDevToolsUsed &&
    isDevMode &&
    showInfo(`${name} load time ${fetchTiming} ms`)
}

export const isDisabledMenuItem = (appConfig: ConfigType, name = '') => {
  return appConfig?.disabledMenuItems?.includes(name.toLocaleLowerCase())
}

export const splitTilesIntoGroups = <T extends { id: string }>(
  n: number,
  data: T[],
) => {
  const result = []
  for (let i = 0; i < data.length; i += n) {
    result.push(data.slice(i, i + n))
  }
  return result
}

export const valueInRange = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

export const createHtmlWidgetUrl = (
  url = '',
  htmlWidgetSize: HtmlWidgetSizeType,
) => {
  if (url && htmlWidgetSize) {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}containerWidth=${htmlWidgetSize.width}&containerHeight=${htmlWidgetSize.height}`
  }
  return url
}

export const addThemeForPage = (
  updatedPage: PageType,
  currentPage: Nullable<PageType>,
) => {
  if (!currentPage || updatedPage.backgroundImage || updatedPage.palette) {
    return updatedPage
  }

  const palette = currentPage.palette
  const backgroundImage = currentPage.backgroundImage

  return {
    ...updatedPage,
    backgroundImage,
    palette,
  }
}

export const logStartupTime = () => {
  if (window.appLoadTimestamp !== null) {
    try {
      const componentLoadTimestamp = Date.now()
      const delta = componentLoadTimestamp - window.appLoadTimestamp
      const message = `[StartupTime] Load time to interactive ${delta}`
      isDevMode && console.log(`⏳ ${message}`)
      isDevMode && showInfo(message)
      window.appLoadTimestamp = null
    } catch (error) {}
  }
}

export const getHomeTabPageParams = (page: PageType, pageId?: string) => {
  const homeTabs = page.homeTab
  if (!homeTabs) {
    return
  }

  const tabs = homeTabs.map(({ id }) => {
    return {
      tabId: id,
    }
  })

  return getPageParams({
    pageId,
    tabs,
    homeTabs,
  })
}

export const getParentContainerFocusId = (
  pathname: string,
  isPopup?: boolean,
) => {
  const isClassicHome = pathname.includes(paths.CLASSIC_HOME)
  const isHome = pathname.includes(paths.HOME)
  const isHomePage = isClassicHome || isHome
  const homeFocusId = `${focusIds.CONTENT}`

  const pageFocusIdPrefix = isPopup ? focusIds.PAGE_POPUP : ''
  const parentFocusId =
    isHomePage && !isPopup
      ? homeFocusId
      : `${pageFocusIdPrefix}${focusIds.PAGE}${focusIds.CONTAINER_MOD}PageContainer`

  return parentFocusId
}
